// Which of your playlists are on the server, and on what terms. Signed in,
// every playlist is LINKED to its row (Sep 2026, playlists follow the
// account): the copy on this device is a working copy of one playlist on the
// server, which your other devices, and everyone holding its link while it is
// shared, edit too (src/data/sharedPlaylistSync.ts keeps the two in step).
// Pure.

import type { PlaylistEntry } from './playlists';
import type { PlaylistContent } from './playlistMerge';

/** What a link holder may do: nothing (a private playlist, yours alone), edit
 *  (the default a sharer gets) or only view. */
export type LinkAccess = 'none' | 'view' | 'edit';

/** The choices a sharer makes once sharing is on. */
export type ShareAccess = Exclude<LinkAccess, 'none'>;

export interface PlaylistLink {
  /** The link's key (`/setlist/<shareId>`). */
  shareId: string;
  /** What the link lets holders do. The owner always edits; 'none' is a
   *  private playlist of your own. */
  access: LinkAccess;
  /** This account shared it: it may change the access or stop sharing. */
  mine: boolean;
  /** The server's revision of `base`. */
  revision: number;
  /** The playlist as it last stood on the server, the common ancestor a merge
   *  needs when this device and the server have both changed since. */
  base: PlaylistContent;
}

export type PlaylistLinks = Record<string, PlaylistLink>;

/** Can this device change the playlist? Everything unlinked, yours, or shared
 *  with edit access; a view-only link is read only. */
export function canEditPlaylist(links: PlaylistLinks, playlistId: string): boolean {
  const link = links[playlistId];
  return !link || link.mine || link.access === 'edit';
}

/** Is the playlist shared: yours with sharing on, or someone else's? */
export function isSharedLink(link: PlaylistLink | undefined): boolean {
  return !!link && (!link.mine || link.access !== 'none');
}

/** A link access read from anywhere: an unknown value is the old default. */
export function linkAccessOf(value: unknown): LinkAccess {
  return value === 'none' || value === 'view' ? value : 'edit';
}

function isEntry(value: unknown): value is PlaylistEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  const ref = entry.ref as Record<string, unknown> | undefined;
  return typeof entry.entryId === 'string' && !!ref && typeof ref.kind === 'string';
}

/** A persisted (or another tab's) links map, guarded field by field. */
export function normalizePlaylistLinks(raw: unknown): PlaylistLinks {
  const out: PlaylistLinks = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const link = value as Record<string, unknown>;
    const base = link.base as Record<string, unknown> | undefined;
    if (typeof link.shareId !== 'string' || !link.shareId) continue;
    if (typeof link.revision !== 'number' || !Number.isFinite(link.revision)) continue;
    if (!base || typeof base.name !== 'string' || !Array.isArray(base.entries)) continue;
    out[id] = {
      shareId: link.shareId,
      access: linkAccessOf(link.access),
      mine: link.mine === true,
      revision: link.revision,
      base: { name: base.name, entries: base.entries.filter(isEntry) },
    };
  }
  return out;
}

/** One of your playlists deleted here, waiting to be deleted from your
 *  account (the device may be offline when you delete it). */
export interface PlaylistDelete {
  shareId: string;
  deletedAt: string;
}

/** A persisted (or another tab's) list of pending deletions, guarded. */
export function normalizePlaylistDeletes(raw: unknown): PlaylistDelete[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: PlaylistDelete[] = [];
  for (const value of raw) {
    if (!value || typeof value !== 'object') continue;
    const d = value as Record<string, unknown>;
    if (typeof d.shareId !== 'string' || !d.shareId || seen.has(d.shareId)) continue;
    seen.add(d.shareId);
    out.push({
      shareId: d.shareId,
      deletedAt: typeof d.deletedAt === 'string' ? d.deletedAt : new Date(0).toISOString(),
    });
  }
  return out;
}
